/**
 * ActionFlowEngine v2 — strict flow execution with stateContract enforcement.
 *
 * Key differences from v1:
 * - NO alias handling (only "params", "target", "append" — no "config", "path", "push")
 * - stateContract enforced on every setState
 * - Simplified action set (same builtins, strict param names)
 */

import type { Flow, FlowStep, BasicStep, ConditionalStep, LoopStep, ParallelStep, TryCatchStep, SwitchStep } from "@cogo/types-v2"
import type { GeminiContent } from "./gemini"
import { GeminiClient, ToolCallbackHandler, buildFunctionDeclarations } from "./gemini"
import type { PlatformAdapter as FullPlatformAdapter } from "./platform-adapter-base"
import { StateManager } from "./state-manager"
import { ComputeEvaluator, isTruthy } from "./compute-evaluator"
import { StoreActionRegistry } from "./store-action-registry"

export type ToastHandler = (params: { message: string; description?: string; variant?: string }) => void

/**
 * v2 PlatformAdapter — re-exports v1 full interface (27 methods).
 * Implementations: WebPlatformAdapter, NativePlatformAdapter, NoopAdapter.
 * Optional methods (openAuthSession) are added as extension.
 */
export interface PlatformAdapter extends FullPlatformAdapter {
  /** OAuth session via in-app browser (RN: expo-web-browser, Web: popup) */
  openAuthSession?(url: string, redirectUrl?: string): Promise<{ type: string; url?: string }>
}

export class ActionFlowEngine {
  private state: StateManager
  private compute: ComputeEvaluator
  private builtinActions: Record<string, (params: Record<string, unknown>, context?: Record<string, unknown>) => unknown | Promise<unknown>>
  private debounceTimers = new Map<string, ReturnType<typeof setTimeout>>()
  private flowRegistry: Record<string, Flow> = {}
  private platform: PlatformAdapter | null = null
  private storeActionRegistry: StoreActionRegistry | null = null

  onToast: ToastHandler | null = null
  onShowPopup: ((params: { title: string; message: string; type?: string }) => void) | null = null
  onShowLoading: ((show: boolean) => void) | null = null

  constructor(state: StateManager, platform?: PlatformAdapter) {
    this.state = state
    this.compute = state.getCompute()
    this.platform = platform ?? null
    this.builtinActions = {
      setState: (params) => this.actionSetState(params),
      navigate: (params) => this.actionNavigate(params),
      showToast: (params) => this.actionShowToast(params),
      delay: (params) => this.actionDelay(params),
      restApi: (params) => this.actionRestApi(params),
      setVariable: (params) => this.actionSetVariable(params),
      log: (params) => this.actionLog(params),
      stopFlow: () => this.actionStopFlow(),
      callFlow: (params, ctx) => this.actionCallFlow(params, ctx),
      compute: (params) => this.actionCompute(params),
      showPopup: (params) => this.actionShowPopup(params),
      showLoading: (params) => this.actionShowLoading(params),
      goLink: (params) => this.actionGoLink(params),
      copyToClipboard: (params) => this.actionCopyToClipboard(params),
      scrollToPosition: (params) => this.actionScrollToPosition(params),
      geminiAction: (params) => this.actionGemini(params),
      geminiFunctionCall: (params) => this.actionGeminiFunctionCall(params),
      geminiVision: (params) => this.actionGeminiVision(params),
      // ── Platform-delegated actions (v18.0 cross-platform parity) ──
      storage: (params) => this.actionStorage(params),
      clipboardRead: (params) => this.actionClipboardRead(params),
      shareAction: (params) => this.actionShare(params),
      geolocation: (params) => this.actionGeolocation(params),
      downloadAction: (params) => this.actionDownload(params),
      fileUpload: (params) => this.actionFileUpload(params),
      notification: (params) => this.actionNotification(params),
      cryptoAction: (params) => this.actionCrypto(params),
      printAction: () => this.actionPrint(),
      analyticsEvent: (params) => this.actionAnalytics(params),
      openAuthSession: (params) => this.actionOpenAuthSession(params),
      payment: (params) => this.actionPayment(params),
      mediaCapture: (params) => this.actionMediaCapture(params),
      websocket: (params) => this.actionWebSocket(params),
      dispatchStoreAction: (params) => this.actionDispatchStoreAction(params),
      // Aliases: flow.json may use these as standalone action names
      // instead of setState with operation parameter
      upsertWhere: (params) => this.actionSetState({ ...params, operation: "upsert", match: params.matchExpr }),
      removeWhere: (params) => this.actionSetState({ ...params, operation: "removeWhere" }),
      updateWhere: (params) => this.actionSetState({ ...params, operation: "updateWhere", update: params.updates }),
      validate: (params) => this.actionValidate(params),
    }
  }

  /** Expose state interface for external action registrations (e.g., Supabase) per D-03 */
  get stateAccessor(): { setState(path: string, value: unknown): void; resolve(template: string): unknown } {
    return {
      setState: (path, value) => this.state.setState(path, value),
      resolve: (template) => this.compute.evaluate(template),
    }
  }

  /** Expose compute interface for external action registrations */
  get computeAccessor(): { evaluate(expr: string): unknown } {
    return this.compute
  }

  setPlatform(platform: PlatformAdapter): void {
    this.platform = platform
  }

  /**
   * Attach a StoreActionRegistry so cogo JSON flows can invoke host store
   * actions via `dispatchStoreAction` with a string handler name.
   * Pass `null` to detach (e.g. on unmount).
   */
  setStoreActionRegistry(registry: StoreActionRegistry | null): void {
    this.storeActionRegistry = registry
  }

  getStoreActionRegistry(): StoreActionRegistry | null {
    return this.storeActionRegistry
  }

  registerFlows(flows: Record<string, Flow>): void {
    Object.assign(this.flowRegistry, flows)
  }

  getRegisteredFlowNames(): string[] {
    return Object.keys(this.flowRegistry)
  }

  findFlow(flowName: string): Flow | undefined {
    return this.flowRegistry[flowName]
      ?? Object.entries(this.flowRegistry).find(([k]) => k.endsWith(`.${flowName}`))?.[1]
  }

  async executeFlow(flow: Flow, context?: Record<string, unknown>): Promise<void> {
    if (flow.debounce && flow.debounce > 0 && flow.name) {
      return new Promise<void>((resolve) => {
        const existing = this.debounceTimers.get(flow.name!)
        if (existing) clearTimeout(existing)
        this.debounceTimers.set(
          flow.name!,
          setTimeout(async () => {
            this.debounceTimers.delete(flow.name!)
            await this.executeFlowImmediate(flow, context)
            resolve()
          }, flow.debounce),
        )
      })
    }
    return this.executeFlowImmediate(flow, context)
  }

  clearDebounceTimers(): void {
    for (const timer of this.debounceTimers.values()) {
      clearTimeout(timer)
    }
    this.debounceTimers.clear()
  }

  registerAction(
    name: string,
    handler: (params: Record<string, unknown>) => unknown | Promise<unknown>,
  ): void {
    this.builtinActions[name] = handler
  }

  // ---- Flow execution ----

  private async executeFlowImmediate(flow: Flow, context?: Record<string, unknown>): Promise<void> {
    try {
      for (const step of flow.steps) {
        await this.executeStep(step, context ?? {})
      }
    } catch (e) {
      if (e && typeof e === "object" && (e as Record<string, unknown>).__stopFlow) return
      throw e
    }
  }

  private async executeStep(step: FlowStep, context: Record<string, unknown>): Promise<void> {
    const stepType = step.type || "basic"
    switch (stepType) {
      case "basic":
        await this.executeBasic(step as BasicStep, context)
        break
      case "conditional":
        await this.executeConditional(step as ConditionalStep, context)
        break
      case "expression":
        await this.executeBasic(step as unknown as BasicStep, context)
        break
      case "loop":
        await this.executeLoop(step as LoopStep, context)
        break
      case "parallel":
        await this.executeParallel(step as ParallelStep, context)
        break
      case "tryCatch":
        await this.executeTryCatch(step as TryCatchStep, context)
        break
      case "switch":
        await this.executeSwitch(step as SwitchStep, context)
        break
      default:
        console.warn(`[runtime-v2] Unknown step type: ${stepType}`)
        break
    }
  }

  private async executeBasic(step: BasicStep, context: Record<string, unknown>): Promise<void> {
    const rawParams = step.params
    const resolvedParams = this.resolveParams(rawParams, context)

    // Restore raw #{item.*} refs — these are resolved per-item in StateManager.
    // But pre-resolve #{event.*} refs since StateManager doesn't have event context.
    if (step.action === "setState" && rawParams.operation === "upsert" && rawParams.onMatch) {
      resolvedParams.onMatch = this.resolveEventRefsInObject(rawParams.onMatch as Record<string, unknown>, context)
    }
    if (step.action === "setState" && rawParams.operation === "updateWhere" && rawParams.update) {
      resolvedParams.update = this.resolveEventRefsInObject(rawParams.update as Record<string, unknown>, context)
    }
    // Same handling for standalone action aliases (upsertWhere)
    // onMatch contains #{item.*} refs that must stay raw for per-item evaluation
    if (step.action === "upsertWhere" && rawParams.onMatch) {
      resolvedParams.onMatch = this.resolveEventRefsInObject(rawParams.onMatch as Record<string, unknown>, context)
    }
    // For standalone updateWhere, updates are fully resolved by resolveParams
    // (no #{item.*} refs to preserve), so no override needed

    // Restore raw expression for compute action
    if (step.action === "compute" && rawParams.expression) {
      let expr = String(rawParams.expression)
      if (expr.startsWith("#{") && expr.endsWith("}")) {
        expr = expr.slice(2, -1)
      }
      resolvedParams.expression = this.resolveEventRefs(expr, context)
    }

    const action = this.builtinActions[step.action]
    if (action) {
      const result = await action(resolvedParams, context)
      // Auto-capture handler return value at _results.<step.name>.
      // Opt-in: only when handler returns non-undefined AND step.name is set.
      if (result !== undefined && step.name) {
        this.state.setState(`_results.${step.name}`, result)
      }
    } else {
      console.warn(`[runtime-v2] Unknown action: ${step.action}`)
    }
  }

  private async executeConditional(step: ConditionalStep, context: Record<string, unknown>): Promise<void> {
    const resolvedCondition = String(this.resolveValue(step.condition, context) ?? step.condition)
    const result = this.compute.evaluate(resolvedCondition)
    const branch = isTruthy(result) ? step.then : (step.else ?? [])
    for (const s of branch) {
      await this.executeStep(s, context)
    }
  }

  private async executeLoop(step: LoopStep, context: Record<string, unknown>): Promise<void> {
    const collection = this.resolveValue(step.collection, context)
    if (!Array.isArray(collection)) return
    for (const item of collection) {
      const loopCtx = { ...context, [step.itemVar]: item }
      for (const s of step.steps) {
        await this.executeStep(s, loopCtx)
      }
    }
  }

  private async executeParallel(step: ParallelStep, context: Record<string, unknown>): Promise<void> {
    await Promise.all(step.steps.map((s) => this.executeStep(s, context)))
  }

  private async executeTryCatch(step: TryCatchStep, context: Record<string, unknown>): Promise<void> {
    try {
      for (const s of step.try) {
        await this.executeStep(s, context)
      }
    } catch (e) {
      const errorContext = { ...context, error: { message: String(e) } }
      for (const s of step.catch) {
        await this.executeStep(s, errorContext)
      }
    }
  }

  private async executeSwitch(step: SwitchStep, context: Record<string, unknown>): Promise<void> {
    const resolved = String(this.resolveValue(step.value, context) ?? "")
    const branch = step.cases[resolved] ?? step.default ?? []
    for (const s of branch) {
      await this.executeStep(s, context)
    }
  }

  // ---- Param Resolution ----

  private resolveParams(params: Record<string, unknown>, ctx: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(params)) {
      result[key] = this.resolveValue(value, ctx)
    }
    return result
  }

  private resolveValue(value: unknown, ctx: Record<string, unknown>): unknown {
    if (typeof value === "string") {
      const localMatch = /^@\{([^}]+)\}$/.exec(value)
      if (localMatch) {
        const inner = localMatch[1].trim()
        if (inner.startsWith("!")) return !this.state.get(inner.slice(1))
        return this.state.get(inner)
      }
      if (value.includes("@{") && !value.includes("#{")) {
        return value.replace(/@\{([^}]+)\}/g, (_, inner) => {
          const path = (inner as string).trim()
          const val = this.state.get(path)
          return val != null ? String(val) : ""
        })
      }
      const pureMatch = /^#\{([^}]+)\}$/.exec(value)
      if (pureMatch) {
        const inner = pureMatch[1].trim()
        const ctxVal = this.getFromContext(inner, ctx)
        if (ctxVal !== undefined) return ctxVal
        const stateVal = this.state.get(inner)
        if (stateVal !== undefined) return stateVal
        if (/[+\-*/<>=?!|&(]/.test(inner)) {
          return this.compute.evaluate(this.resolveEventRefs(inner, ctx))
        }
        return undefined
      }
      if (value.includes("#{")) {
        let result: string = value.replace(/#\{([^}]+)\}/g, (_, ref) => {
          const r = (ref as string).trim()
          const ctxVal = this.getFromContext(r, ctx)
          let resolved = ctxVal !== undefined ? ctxVal : this.state.get(r)
          if (resolved == null && /[+\-*/<>=?!|&(]/.test(r)) {
            resolved = this.compute.evaluate(this.resolveEventRefs(r, ctx))
          }
          if (resolved == null) return ""
          return String(resolved)
        })
        if (result.includes("@{")) {
          result = result.replace(/@\{([^}]+)\}/g, (_, inner) => {
            const path = (inner as string).trim()
            const val = this.state.get(path)
            return val != null ? String(val) : ""
          })
        }
        return result
      }
      return value
    }
    if (Array.isArray(value)) {
      return value.map((v) => this.resolveValue(v, ctx))
    }
    if (value !== null && typeof value === "object") {
      return this.resolveParams(value as Record<string, unknown>, ctx)
    }
    return value
  }

  /**
   * Resolve #{event.*} refs in onMatch/update objects while preserving #{item.*} refs.
   * This ensures event context is available for per-item expressions like #{item.quantity + event.quantity}.
   */
  private resolveEventRefsInObject(obj: Record<string, unknown>, ctx: Record<string, unknown>): Record<string, unknown> {
    const result: Record<string, unknown> = {}
    for (const [key, val] of Object.entries(obj)) {
      if (typeof val === "string" && val.includes("#{")) {
        // Replace #{event.xxx} with actual values, keep #{item.xxx} as-is
        result[key] = val.replace(/#\{([^}]+)\}/g, (full, inner) => {
          const expr = (inner as string).trim()
          // Pure event ref: #{event.quantity} → resolve completely
          if (/^event\.\w+$/.test(expr)) {
            const resolved = this.getFromContext(expr, ctx)
            if (resolved !== undefined) return `#{${resolved}}`  // wrap as literal for StateManager
            return full
          }
          // Mixed: #{item.quantity + event.quantity} → resolve event refs inline
          if (expr.includes("event.")) {
            const resolved = this.resolveEventRefs(expr, ctx)
            return `#{${resolved}}`
          }
          return full  // pure item refs: keep as-is
        })
      } else {
        result[key] = val
      }
    }
    return result
  }

  private resolveEventRefs(expr: string, ctx: Record<string, unknown>): string {
    const event = ctx.event
    if (event == null || typeof event !== "object") return expr
    const ev = event as Record<string, unknown>
    return expr.replace(/\bevent\.(\w+)/g, (_m, key) => {
      const val = ev[key as string]
      if (val === undefined) return _m
      if (typeof val === "string") return `'${val}'`
      return String(val)
    })
  }

  private getFromContext(path: string, ctx: Record<string, unknown>): unknown {
    const keys = path.split(".")
    let current: unknown = ctx
    for (const key of keys) {
      if (current == null || typeof current !== "object") return undefined
      current = (current as Record<string, unknown>)[key]
    }
    return current
  }

  // ---- Built-in Actions (strict v2 — no aliases) ----

  /** setState — strict "target" only (no "path" alias). */
  private actionSetState(params: Record<string, unknown>): void {
    const { operation, value } = params as {
      target: string
      operation?: string
      value?: unknown
    }
    const target = (params.target as string).replace(/^#/, "")

    switch (operation) {
      case "append":
        this.state.appendTo(target, value)
        break
      case "upsert": {
        const match = params.match as string | undefined
        const onMatch = params.onMatch as Record<string, unknown> | undefined
        const onNew = params.onNew as Record<string, unknown> | undefined
        this.state.upsertWhere(target, match, onMatch, onNew)
        break
      }
      case "removeWhere": {
        const predicate = params.predicate as string | undefined
        if (predicate) {
          this.state.removeWhere(target, (item) =>
            this.compute.evalPredicate(predicate, item),
          )
        }
        break
      }
      case "updateWhere": {
        const pred = params.predicate as string | undefined
        const update = params.update as Record<string, unknown> | undefined
        if (pred && update) {
          this.state.updateWhere(
            target,
            (item) => this.compute.evalPredicate(pred, item),
            update,
          )
        }
        break
      }
      default:
        if (
          value !== null &&
          typeof value === "object" &&
          !Array.isArray(value)
        ) {
          const current = this.state.get(target)
          if (
            current !== null &&
            typeof current === "object" &&
            !Array.isArray(current)
          ) {
            this.state.setState(target, { ...(current as Record<string, unknown>), ...(value as Record<string, unknown>) })
            return
          }
        }
        this.state.setState(target, value)
    }
  }

  private actionNavigate(params: Record<string, unknown>): void {
    if (!this.platform) return
    const mode = (params.mode as string) ?? "push"
    const route = (params.route ?? params.target) as string | undefined
    switch (mode) {
      case "replace":
        if (route) this.platform.replaceRoute(route)
        break
      case "push":
      default:
        if (route) this.platform.pushRoute(route)
        break
    }
  }

  private actionShowToast(params: Record<string, unknown>): void {
    if (this.onToast) {
      this.onToast({
        message: String(params.message ?? ""),
        description: params.description ? String(params.description) : undefined,
        variant: params.variant ? String(params.variant) : undefined,
      })
    }
  }

  private actionDelay(params: Record<string, unknown>): Promise<void> {
    const ms = Number(params.duration ?? params.ms ?? 0)
    return new Promise((resolve) => setTimeout(resolve, ms))
  }

  private async actionRestApi(params: Record<string, unknown>): Promise<void> {
    const url = String(params.url ?? "")
    const method = String(params.method ?? "GET").toUpperCase()
    const headers = (params.headers as Record<string, string>) ?? {}
    const body = params.body
    const target = (params.resultTarget ?? params.target) as string | undefined
    const credentials = params.credentials as RequestCredentials | undefined

    const fetchOptions: RequestInit = { method, headers }
    if (credentials) fetchOptions.credentials = credentials
    if (body && method !== "GET") {
      fetchOptions.body = JSON.stringify(body)
      if (!headers["Content-Type"]) {
        ;(fetchOptions.headers as Record<string, string>)["Content-Type"] = "application/json"
      }
    }

    const resp = await fetch(url, fetchOptions)
    const data = await resp.json().catch(() => null)

    if (target) {
      // Parse Content-Range header for PostgREST count support
      const contentRange = resp.headers.get("content-range")
      let count: number | null = null
      if (contentRange) {
        const parts = contentRange.split("/")
        const total = parts[1]
        if (total && total !== "*") count = parseInt(total, 10)
      }

      // Normalize response so #{_results.*.data} resolves correctly.
      // Cloudflare pg-proxy already wraps as {success, data, count}.
      // Raw PostgREST returns plain arrays. Handle both cases:
      let wrapped: Record<string, unknown>
      if (data && typeof data === "object" && !Array.isArray(data) && "data" in data) {
        // Already wrapped (e.g., pg-proxy response {success, data, count}) — use as-is
        // but merge count from Content-Range if proxy didn't include it
        wrapped = { ...data, status: resp.status }
        if (count != null && wrapped.count == null) wrapped.count = count
      } else {
        // Raw response (array or primitive) — wrap for #{_results.*.data} pattern
        wrapped = { data, count, status: resp.status }
      }
      this.state.setState(target.replace(/^#/, ""), wrapped)
    }
  }

  private actionSetVariable(params: Record<string, unknown>): void {
    const name = String(params.name ?? params.target ?? "")
    const value = params.value
    if (name) {
      this.state.setState(name.replace(/^#/, ""), value)
    }
  }

  private actionLog(params: Record<string, unknown>): void {
    const level = String(params.level ?? "info")
    const message = params.message ?? params.value
    switch (level) {
      case "warn": console.warn("[cogo-v2]", message); break
      case "error": console.error("[cogo-v2]", message); break
      default: console.log("[cogo-v2]", message)
    }
  }

  private actionStopFlow(): never {
    throw { __stopFlow: true }
  }

  /**
   * dispatchStoreAction — invoke a host-registered handler by name.
   *
   * params.handler: string — must be pre-registered via StoreActionRegistry.
   * params.params?: object — passed as-is to the handler.
   *
   * Unknown handler / missing registry / non-string handler name all produce
   * a console.warn and resolve without throwing, so a buggy flow cannot crash
   * the app. Registered handler exceptions propagate normally.
   */
  private async actionDispatchStoreAction(params: Record<string, unknown>): Promise<void> {
    const name = typeof params.handler === "string" ? params.handler : null
    if (!name) {
      console.warn("[runtime-v2] dispatchStoreAction: missing or non-string 'handler' param", params)
      return
    }
    const registry = this.storeActionRegistry
    if (!registry) {
      console.warn(`[runtime-v2] dispatchStoreAction: no StoreActionRegistry attached (handler="${name}")`)
      return
    }
    const handler = registry.get(name)
    if (!handler) {
      console.warn(
        `[runtime-v2] dispatchStoreAction: unknown handler "${name}" — not in registry. ` +
          `Known: [${registry.list().join(", ")}]`,
      )
      return
    }
    const handlerParams = params.params && typeof params.params === "object"
      ? (params.params as Record<string, unknown>)
      : undefined
    await handler(handlerParams)
  }

  private async actionCallFlow(params: Record<string, unknown>, parentContext?: Record<string, unknown>): Promise<void> {
    const flowName = params.flow as string ?? params.flowName as string
    const flow = this.findFlow(flowName)
    if (!flow) return
    const ctx = (params.context as Record<string, unknown>) ?? parentContext ?? {}
    await this.executeFlowImmediate(flow, ctx)
  }

  private actionCompute(params: Record<string, unknown>): unknown {
    const target = params.target as string | undefined
    if (!target) return undefined

    // value form: object/array literal already deep-resolved by resolveParams.
    // Takes precedence over expression form when both are present.
    if (params.value !== undefined) {
      this.state.setState(target.replace(/^#/, ""), params.value)
      return params.value
    }

    // expression form: evaluate compute-evaluator string.
    if (params.expression) {
      const result = this.compute.evaluate(String(params.expression))
      this.state.setState(target.replace(/^#/, ""), result)
      return result
    }

    return undefined
  }

  /**
   * Validate `params.target` against `params.rules` (array of {type, value?, message?}).
   * Returns {valid: boolean, message?: string}. First failing rule wins.
   * Unknown rule types are treated as no-ops (forward-compat).
   */
  private actionValidate(params: Record<string, unknown>): { valid: boolean; message?: string } {
    const target = params.target
    const rules = Array.isArray(params.rules) ? (params.rules as Array<Record<string, unknown>>) : []

    for (const rule of rules) {
      const ruleType = String(rule.type ?? "")
      const message = rule.message != null ? String(rule.message) : undefined
      const ruleValue = rule.value

      let pass = true

      switch (ruleType) {
        case "required":
          pass = target !== null && target !== undefined && target !== ""
          break

        case "minLength": {
          const min = Number(ruleValue)
          const len = typeof target === "string" || Array.isArray(target)
            ? (target as { length: number }).length
            : 0
          pass = len >= min
          break
        }

        case "maxLength": {
          const max = Number(ruleValue)
          const len = typeof target === "string" || Array.isArray(target)
            ? (target as { length: number }).length
            : 0
          pass = len <= max
          break
        }

        case "pattern": {
          const re = String(ruleValue ?? "")
          let regex: RegExp | null = null
          try {
            regex = new RegExp(re)
          } catch {
            regex = null  // bad regex → treat as pass (forward-compat)
          }
          pass = regex == null ? true : regex.test(String(target))
          break
        }

        case "email":
          pass = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(target))
          break

        case "min": {
          const min = Number(ruleValue)
          pass = Number(target) >= min
          break
        }

        case "max": {
          const max = Number(ruleValue)
          pass = Number(target) <= max
          break
        }

        default:
          // Unknown rule types pass silently (forward-compat).
          pass = true
      }

      if (!pass) {
        return { valid: false, ...(message ? { message } : {}) }
      }
    }

    return { valid: true }
  }

  private actionShowPopup(params: Record<string, unknown>): void {
    if (this.onShowPopup) {
      this.onShowPopup({
        title: String(params.title ?? ""),
        message: String(params.message ?? ""),
        type: params.type ? String(params.type) : undefined,
      })
    }
  }

  private actionShowLoading(params: Record<string, unknown>): void {
    if (this.onShowLoading) {
      this.onShowLoading(Boolean(params.show ?? params.value ?? true))
    }
  }

  private actionGoLink(params: Record<string, unknown>): void {
    const url = String(params.url ?? "")
    const newTab = Boolean(params.newTab ?? true)
    if (this.platform?.openExternal) {
      this.platform.openExternal(url, newTab)
    }
  }

  private async actionCopyToClipboard(params: Record<string, unknown>): Promise<void> {
    const text = String(params.text ?? params.value ?? "")
    if (this.platform?.copyToClipboard) {
      await this.platform.copyToClipboard(text)
    }
  }

  private actionScrollToPosition(params: Record<string, unknown>): void {
    const y = Number(params.y ?? 0)
    const smooth = Boolean(params.smooth ?? params.behavior !== "instant")
    if (this.platform) {
      this.platform.scrollToPosition(y, smooth)
    }
  }

  // ---- Gemini AI Action (ported from v1) ----

  private async actionGemini(params: Record<string, unknown>): Promise<void> {
    const apiKey = String(params.apiKey ?? '')
    const model = String(params.model ?? 'gemini-2.5-pro')
    const prompt = String(params.prompt ?? '')
    const exposedActions = (params.exposedActions as string[]) ?? []
    const resultTarget = String(params.resultTarget ?? '_results.gemini').replace(/^#/, '')
    const maxToolCalls = Number(params.maxToolCalls ?? 5)

    const client = new GeminiClient({ apiKey, model })
    const callbackHandler = new ToolCallbackHandler(
      async (actionName, actionParams) => {
        const action = this.builtinActions[actionName]
        if (!action) throw new Error(`Unknown action: ${actionName}`)
        await action(actionParams)
        return this.state.get(resultTarget) ?? {}
      },
    )

    const tools = exposedActions.length > 0
      ? [{ functionDeclarations: buildFunctionDeclarations(exposedActions) }]
      : undefined

    const history = (this.state.get('_gemini.history') as GeminiContent[]) ?? []
    const contents: GeminiContent[] = [
      ...history,
      { role: 'user', parts: [{ text: prompt }] },
    ]

    let currentContents: GeminiContent[] = contents
    let toolCallCount = 0

    while (toolCallCount < maxToolCalls) {
      const result = await client.generateContent({
        contents: currentContents,
        ...(tools && { tools }),
      })

      if (result.functionCall) {
        toolCallCount++
        const actionResult = await callbackHandler.handle(result.functionCall)
        const functionResponseContent = callbackHandler.buildFunctionResponseContent(
          actionResult.name,
          actionResult.response,
        )
        currentContents = [
          ...currentContents,
          { role: 'model', parts: [{ functionCall: result.functionCall }] } as GeminiContent,
          functionResponseContent,
        ]
      } else {
        this.state.setState('_gemini.history', currentContents)
        this.state.setState(resultTarget, {
          type: 'text',
          response: result.text ?? '',
          toolCallsUsed: toolCallCount,
        })
        return
      }
    }

    this.state.setState(resultTarget, {
      type: 'error',
      error: `Max tool calls (${maxToolCalls}) reached`,
      toolCallsUsed: toolCallCount,
    })
  }

  // ---- Gemini Function Call Agent Loop ----

  private async actionGeminiFunctionCall(params: Record<string, unknown>): Promise<void> {
    const { agentLoop } = await import('./gemini')
    const apiKey = String(params.apiKey ?? '')
    const model = String(params.model ?? 'gemini-2.5-flash')
    const prompt = String(params.prompt ?? '')
    const maxTurns = Number(params.maxTurns ?? 5)
    const tokenBudget = Number(params.tokenBudget ?? 20000)
    const systemPrompt = params.systemPrompt ? String(params.systemPrompt) : undefined
    const target = String(params.resultTarget ?? '_results.agent').replace(/^#/, '')
    const toolNames = (params.tools as string[]) ?? []

    const client = new GeminiClient({ apiKey, model })
    const toolHandler = new ToolCallbackHandler(
      async (actionName, actionParams) => {
        const action = this.builtinActions[actionName]
        if (!action) throw new Error(`Unknown action: ${actionName}`)
        await action(actionParams)
        return this.state.get(target) ?? {}
      },
    )

    const tools = buildFunctionDeclarations(toolNames)

    try {
      const result = await agentLoop({
        client,
        toolHandler,
        prompt,
        tools,
        maxTurns,
        tokenBudget,
        systemPrompt,
      })

      this.state.setState(target, { data: result.text, error: null })
      this.state.setState('_results.agentHistory', result.history)
    } catch (err: any) {
      this.state.setState(target, { data: null, error: err.message ?? String(err) })
    }
  }

  // ---- Gemini Vision Action ----

  private async actionGeminiVision(params: Record<string, unknown>): Promise<void> {
    const { buildImageParts } = await import('./gemini-vision-action')
    const prompt = String(params.prompt ?? 'Describe this image.')
    const model = String(params.model ?? 'gemini-2.5-flash')
    const apiKey = String(params.apiKey ?? '')
    const target = String(params.resultTarget ?? '_results.vision').replace(/^#/, '')

    try {
      const imageParts = buildImageParts({
        imageUrl: params.imageUrl as string | undefined,
        imageBase64: params.imageBase64 as string | undefined,
        images: params.images as any[] | undefined,
        mimeType: params.mimeType as string | undefined,
      })

      const parts = [...imageParts, { text: prompt }]
      const client = new GeminiClient({ apiKey, model })

      const result = await client.generateContent({
        contents: [{ role: 'user', parts }],
      })

      this.state.setState(target, { data: result.text ?? '', error: null })
    } catch (err: any) {
      this.state.setState(target, { data: null, error: err.message ?? String(err) })
    }
  }

  // ── Platform-delegated actions (v18.0) ──

  private actionStorage(params: Record<string, unknown>): void {
    if (!this.platform) return
    const op = String(params.operation ?? "get")
    const key = String(params.key ?? "")
    const resultPath = String(params.resultPath ?? "")
    if (op === "get") {
      const value = this.platform.getItem(key)
      if (resultPath) this.state.setState(resultPath, value)
    } else if (op === "set") {
      this.platform.setItem(key, String(params.value ?? ""))
    } else if (op === "remove") {
      this.platform.removeItem(key)
    } else if (op === "clear") {
      this.platform.clearStorage()
    }
  }

  private async actionClipboardRead(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultPath = String(params.resultPath ?? "_results.clipboard")
    const text = await this.platform.readClipboard()
    this.state.setState(resultPath, text)
  }

  private async actionShare(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    await this.platform.share({
      title: params.title as string | undefined,
      text: params.text as string | undefined,
      url: params.url as string | undefined,
    })
  }

  private async actionGeolocation(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultPath = String(params.resultPath ?? "_results.geo")
    try {
      const pos = await this.platform.getGeolocation()
      this.state.setState(resultPath, pos)
    } catch {
      this.state.setState(resultPath, { error: "geolocation_failed" })
    }
  }

  private actionDownload(params: Record<string, unknown>): void {
    if (!this.platform) return
    const url = String(params.url ?? "")
    const filename = String(params.filename ?? "download")
    this.platform.download(url, filename)
  }

  private async actionFileUpload(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const accept = params.accept as string | undefined
    const resultPath = String(params.resultPath ?? "_results.file")
    const file = await this.platform.pickFile(accept)
    this.state.setState(resultPath, file)
  }

  private actionNotification(params: Record<string, unknown>): void {
    if (!this.platform) return
    const title = String(params.title ?? "")
    const body = params.body as string | undefined
    this.platform.showNotification(title, body)
  }

  private async actionCrypto(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const algorithm = String(params.algorithm ?? "SHA-256")
    const data = String(params.data ?? "")
    const resultPath = String(params.resultPath ?? "_results.crypto")
    const hash = await this.platform.digest(algorithm, data)
    this.state.setState(resultPath, hash)
  }

  private actionPrint(): void {
    this.platform?.print()
  }

  private actionAnalytics(params: Record<string, unknown>): void {
    if (!this.platform) return
    const event = String(params.event ?? "")
    const data = params.data as Record<string, unknown> | undefined
    this.platform.dispatchAnalyticsEvent(event, data)
  }

  private async actionOpenAuthSession(params: Record<string, unknown>): Promise<void> {
    if (!this.platform?.openAuthSession) {
      // Fallback: open in external browser
      this.platform?.openExternal(String(params.url ?? ""), true)
      return
    }
    const url = String(params.url ?? "")
    const redirectUrl = params.redirectUrl as string | undefined
    const resultTarget = String(params.resultTarget ?? "_results.auth")
    try {
      const result = await this.platform.openAuthSession(url, redirectUrl)
      this.state.setState(resultTarget, result)
    } catch {
      this.state.setState(resultTarget, { type: "error" })
    }
  }

  /** Payment action — delegates to onPayment handler or platform */
  onPayment: ((params: Record<string, unknown>) => Promise<Record<string, unknown>>) | null = null

  private async actionPayment(params: Record<string, unknown>): Promise<void> {
    if (!this.onPayment) return
    const resultTarget = String(params.resultTarget ?? "_results.payment")
    try {
      const result = await this.onPayment(params)
      this.state.setState(resultTarget, result)
    } catch (err: any) {
      this.state.setState(resultTarget, { error: err.message ?? String(err) })
    }
  }

  private async actionMediaCapture(params: Record<string, unknown>): Promise<void> {
    if (!this.platform) return
    const resultPath = String(params.resultPath ?? "_results.media")
    try {
      const media = await this.platform.getUserMedia({
        audio: Boolean(params.audio),
        video: params.type !== "audio",
      })
      this.state.setState(resultPath, media)
    } catch {
      this.state.setState(resultPath, { error: "media_capture_failed" })
    }
  }

  private actionWebSocket(params: Record<string, unknown>): void {
    if (!this.platform) return
    const action = String(params.action ?? "connect")
    const url = String(params.url ?? "")
    const connectionId = String(params.connectionId ?? "default")
    const resultPath = `_results.ws.${connectionId}`

    if (action === "connect") {
      const ws = this.platform.createWebSocket(url)
      ws.onMessage((data) => this.state.setState(`${resultPath}.lastMessage`, data))
      ws.onClose(() => this.state.setState(`${resultPath}.connected`, false))
      this.state.setState(`${resultPath}.connected`, true)
      // Store send/close in state for later use
      this.state.setState(`${resultPath}.__ws`, ws)
    } else if (action === "send") {
      const ws = this.state.resolve(`#{${resultPath}.__ws}`) as any
      if (ws?.send) ws.send(String(params.data ?? ""))
    } else if (action === "close") {
      const ws = this.state.resolve(`#{${resultPath}.__ws}`) as any
      if (ws?.close) ws.close()
    }
  }
}
